import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Stack,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../design-system/tokens/colors';

interface PermissionsData {
  user_id: string;
  manager_type?: string[];
  permissions?: string[];
  removed_manager_types?: string[];
}

interface PermissionsPanelProps {
  permissionsData: {
    data: PermissionsData | null;
    resumo: string;
    url: string;
  };
  operationType?: 'add' | 'remove' | 'error';
}

const getManagerTypeLabel = (managerType: string): string => {
  const labels: Record<string, string> = {
    'gestor_nutricao': 'Gestor de Nutrição',
    'gestor_pasto': 'Gestor de Pastos',
    'gestor_animal': 'Gestor de Animais',
    'gestor_lote': 'Gestor de Lotes',
    'gestor_manejo_lotes': 'Gestor de Manejo de Lotes',
    'gestor_financeiro': 'Gestor Financeiro',
    'gestor_nascimento': 'Gestor de Nascimentos',
    'gestor_sanidade': 'Gestor de Sanidade',
  };
  return labels[managerType] || managerType;
};

const getPermissionLabel = (permission: string): string => {
  const labels: Record<string, string> = {
    'insert': 'Inserir',
    'update': 'Atualizar',
    'delete': 'Excluir',
    'search': 'Consultar',
  };
  return labels[permission] || permission;
};

const getManagerTypeColor = (managerType: string): string => {
  // Usando o azul principal da aplicação
  if (managerType === 'gestor_master') {
    return DESIGN_TOKENS.colors.secondary[700]; // Cinza escuro para o gestor principal
  } else {
    return DESIGN_TOKENS.colors.brand.primary; // Azul principal da aplicação (#004E64)
  }
};

export const PermissionsPanel: React.FC<PermissionsPanelProps> = ({
  permissionsData,
  operationType,
}) => {
  const { data, resumo } = permissionsData;
  
  // Caso de erro (usuário não encontrado, etc.)
  if (operationType === 'error' || !data) {
    return (
      <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
        <Alert
          severity="error"
          icon={<CheckCircleIcon />}
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Operação não realizada ❌
          </Typography>
          <Typography variant="body2">
            {resumo}
          </Typography>
        </Alert>

        <Card
          elevation={2}
          sx={{
            borderRadius: 3,
            border: `1px solid ${DESIGN_TOKENS.colors.status.error[100]}`,
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar
                sx={{
                  bgcolor: DESIGN_TOKENS.colors.status.error[100],
                  color: DESIGN_TOKENS.colors.status.error[600],
                  width: 56,
                  height: 56,
                }}
              >
                <PersonIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.text.primary }}>
                  Usuário não encontrado
                </Typography>
                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  O usuário solicitado não existe na organização
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, textAlign: 'center' }}>
              Verifique se o email está correto e tente novamente.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }
  
  // Determinar automaticamente o tipo de operação se não fornecido
  const isRemoval = operationType === 'remove' || !!data.removed_manager_types;
  const managerTypes = isRemoval ? (data.removed_manager_types || []) : (data.manager_type || []);
  const permissions = data.permissions || [];
  const operationText = isRemoval ? 'Removidas' : 'Atribuídas';
  const operationColor = isRemoval ? 'warning' : 'success';
  const operationIcon = isRemoval ? '🗑️' : '✅';

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header de Sucesso */}
      <Alert
        severity={operationColor as 'success' | 'warning'}
        icon={<CheckCircleIcon />}
        sx={{
          mb: 3,
          borderRadius: 2,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Permissões {operationText} com Sucesso! {operationIcon}
        </Typography>
        <Typography variant="body2">
          {resumo}
        </Typography>
      </Alert>

      {/* Card Principal */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
          border: `1px solid ${DESIGN_TOKENS.colors.status.success[100]}`,
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Informações do Usuário */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: DESIGN_TOKENS.colors.status.success[100],
                color: DESIGN_TOKENS.colors.status.success[700],
                mr: 2,
                width: 48,
                height: 48,
              }}
            >
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Usuário: {data.user_id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Colaborador da fazenda
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Tipos de Gestor */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AssignmentIcon
                sx={{
                  color: DESIGN_TOKENS.colors.primary[600],
                  mr: 1,
                  fontSize: 20,
                }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Tipos de Gestor {operationText}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {managerTypes.map((type, index) => (
                <Chip
                  key={index}
                  label={getManagerTypeLabel(type)}
                  sx={{
                    bgcolor: `${getManagerTypeColor(type)}20`,
                    color: getManagerTypeColor(type),
                    fontWeight: 500,
                    mb: 1,
                    '& .MuiChip-label': {
                      px: 2,
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Permissões */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon
                sx={{
                  color: DESIGN_TOKENS.colors.primary[600],
                  mr: 1,
                  fontSize: 20,
                }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Permissões {isRemoval ? 'Removidas' : 'Concedidas'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {permissions.map((permission, index) => (
                <Chip
                  key={index}
                  label={getPermissionLabel(permission)}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: DESIGN_TOKENS.colors.primary[300],
                    color: DESIGN_TOKENS.colors.primary[700],
                    mb: 1,
                    '& .MuiChip-label': {
                      fontWeight: 500,
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Informações Adicionais - apenas para adição */}
      {!isRemoval && (
        <Card
          elevation={1}
          sx={{
            borderRadius: 2,
            bgcolor: DESIGN_TOKENS.colors.neutral[50],
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              💡 <strong>Próximos passos:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • O usuário agora pode acessar os gestores atribuídos no chat
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • As permissões são aplicadas automaticamente
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Você pode gerenciar essas permissões a qualquer momento
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PermissionsPanel;